import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_USERS } from "../../../utils/constants";
import { UserInterface } from "../../../models/UserModel/User";
import userService from "../../../services/userService/userService";

interface UpdateUsersContext {
  previousUsers: UserInterface[];
}

const useUpdateUser = (
  page: number,
  pageSize: number,
  firstTimeLogin?: boolean
) => {
  const queryClient = useQueryClient();

  return useMutation<UserInterface, Error, UserInterface, UpdateUsersContext>({
    mutationFn: async (user: UserInterface) => {
      // Assuming the ID is stored in the priority object or can be derived
      const { id, ...userData } = user;
      return userService.Update(userData);
    },
    onMutate: async (updatedUser: UserInterface) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_USERS, page, pageSize],
      });

      const previousUsers =
        queryClient.getQueryData<UserInterface[]>([
          CACHE_KEY_USERS,
          page,
          pageSize,
        ]) || [];

      queryClient.setQueryData<UserInterface[]>(
        [CACHE_KEY_USERS, page, pageSize],
        (users = []) =>
          users.map((user) => (user.id === updatedUser.id ? updatedUser : user))
      );

      return { previousUsers };
    },

    onSuccess: (savedUser) => {
      queryClient.setQueryData<UserInterface[]>(
        [CACHE_KEY_USERS, page, pageSize],
        (users) =>
          users?.map((user) => (user.id === savedUser.id ? savedUser : user))
      );
    },

    onError: (error, updatedUser, context) => {
      if (!context) return;

      queryClient.setQueryData<UserInterface[]>(
        [CACHE_KEY_USERS, page, pageSize],
        context.previousUsers
      );
    },
  });
};

export default useUpdateUser;
